// ----- Phase 5.X-background-validation-ui — ValidationScoreBadge -----
//
// Compact live-score badge for the CompilerPage header. Polls the
// /validation-status endpoint every 5 s (via useValidationStatus) when
// the continuousValidation setting is on, showing:
//
//   🔄 Validating…       — in-flight (status=running)
//   ✅ Valid             — no open issues (errorCount=0 && warningCount=0)
//   N error(s)           — red pill when errorCount > 0
//   N warning(s)         — amber pill when no errors but warningCount > 0
//
// Clicking the badge navigates the user to the ValidationStatusBanner by
// scrolling to (or focusing) the banner element. Pass `onBadgeClick` from
// the parent to trigger that scroll/focus.

import { type CSSProperties, type ReactElement } from 'react';
import { useValidationStatus } from '@/hooks/useValidationStatus';
import { formatValidationCounts } from '@/lib/validationSummary';

export interface ValidationScoreBadgeProps {
  readonly submissionId: string | null | undefined;
  /** Called when the user clicks the badge — typically scrolls to / focuses the validation banner. */
  readonly onBadgeClick?: () => void;
}

const base: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 4,
  padding: '3px 10px',
  borderRadius: 12,
  fontSize: 12,
  fontWeight: 600,
  cursor: 'pointer',
  border: 'none',
  outline: 'none',
  userSelect: 'none',
};

const styleRunning: CSSProperties = {
  ...base,
  background: 'rgba(99,102,241,0.12)',
  color: '#4338ca',
  border: '1px solid rgba(99,102,241,0.35)',
  cursor: 'default',
};

const styleValid: CSSProperties = {
  ...base,
  background: 'rgba(34,197,94,0.10)',
  color: '#15803d',
  border: '1px solid rgba(34,197,94,0.35)',
};

const styleError: CSSProperties = {
  ...base,
  background: 'rgba(239,68,68,0.10)',
  color: '#b91c1c',
  border: '1px solid rgba(239,68,68,0.35)',
};

const styleWarning: CSSProperties = {
  ...base,
  background: 'rgba(245,158,11,0.10)',
  color: '#92400e',
  border: '1px solid rgba(245,158,11,0.35)',
};

const styleIdle: CSSProperties = {
  ...base,
  background: 'var(--surface2)',
  color: 'var(--text-muted)',
  border: '1px solid var(--border)',
  cursor: 'default',
};

// ISS-017 — a run EXISTS but is not current (stale after an edit, or
// per-document checks only). Amber like warnings, but dashed so it never
// reads as a current-run count.
const styleStale: CSSProperties = {
  ...base,
  background: 'rgba(245,158,11,0.08)',
  color: '#92400e',
  border: '1px dashed rgba(245,158,11,0.5)',
};


export function ValidationScoreBadge({
  submissionId,
  onBadgeClick,
}: ValidationScoreBadgeProps): ReactElement {
  const { status, errorCount, warningCount, lastRunAt, currency, loading } =
    useValidationStatus(submissionId);

  if (!submissionId || loading) {
    return (
      <span
        data-testid="validation-score-badge-loading"
        style={styleIdle}
        aria-label="Loading validation status"
      >
        …
      </span>
    );
  }

  if (status === 'running') {
    return (
      <span
        data-testid="validation-score-badge-running"
        style={styleRunning}
        aria-label="Validating in progress"
        role="status"
      >
        🔄 Validating…
      </span>
    );
  }

  if (status === 'idle') {
    // ISS-017 — 'idle' conflates two states (T17-D43): NO run at all
    // (lastRunAt null) and a run that exists but is not current (stale after
    // a document edit, or per-document checks only). Claiming "not validated"
    // for the second made the toolbar say "No validation run yet" while the
    // report/drawer showed that run's 4 errors / 1 warning — the same run,
    // two stories. A run that exists is shown as STALE, with its counts in
    // the one shared phrasing, so every surface describes the same run.
    if (lastRunAt === null) {
      return (
        <span
          data-testid="validation-score-badge-idle"
          style={styleIdle}
          aria-label="No validation run yet"
        >
          — not validated
        </span>
      );
    }
    const label = formatValidationCounts({ errorCount, warningCount });
    const detail =
      currency?.detail ?? 'Documents changed since this run; validate again.';
    return (
      <button
        type="button"
        data-testid="validation-score-badge-stale"
        style={styleStale}
        onClick={onBadgeClick}
        title={detail}
        aria-label={`${label} — out of date (${detail}) — click to view results`}
      >
        {label} · stale
      </button>
    );
  }

  // status === 'complete'
  if (errorCount === 0 && warningCount === 0) {
    return (
      <button
        type="button"
        data-testid="validation-score-badge-valid"
        style={styleValid}
        onClick={onBadgeClick}
        aria-label="Validation passed — click to view results"
      >
        ✅ Valid
      </button>
    );
  }

  // phase-validation-surfaces item 2 — phrasing comes from the one shared
  // formatter so this badge, the top-bar chip and the bottom worklist can never
  // word the same run differently. (This badge already showed both counts; the
  // change keeps it in lockstep with the surfaces that were fixed to.)
  if (errorCount > 0) {
    const label = formatValidationCounts({ errorCount, warningCount });
    return (
      <button
        type="button"
        data-testid="validation-score-badge-error"
        style={styleError}
        onClick={onBadgeClick}
        aria-label={`${label} — click to view results`}
      >
        {label}
      </button>
    );
  }

  // warningCount > 0, no errors
  const label = formatValidationCounts({ errorCount, warningCount });
  return (
    <button
      type="button"
      data-testid="validation-score-badge-warning"
      style={styleWarning}
      onClick={onBadgeClick}
      aria-label={`${label} — click to view results`}
    >
      {label}
    </button>
  );
}
